/**
 * Admin → Devices.
 *
 * - Tabela principal: hardware cadastrado (probes, tomadas, sensores).
 * - Seção "Devices órfãos": entidades publicando MQTT mas não cadastradas.
 *   Útil pra descobrir hardware novo no chão.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  Building2,
  LayoutGrid,
  Pencil,
  Replace,
  Plus,
  Rows3,
  Trash2,
} from "lucide-react";
import toast from "react-hot-toast";
import clsx from "clsx";

import { devicesApi } from "@/api/devices";
import { apiErrorMessage } from "@/api/client";
import {
  qk,
  useDeviceStatuses,
  useDeviceSummary,
  useDevices,
  useEquipmentKinds,
  useEquipments,
  useOrphanDevices,
  useSites,
  useTenants,
} from "@/hooks/queries";
import { useRotuloDeUnidade } from "@/hooks/useRotuloDeUnidade";
import { useAuth } from "@/contexts/AuthContext";
import type {
  Device,
  DeviceCreate,
  DeviceStatus,
  Equipment,
  OrphanDevice,
  PowerSource,
  Site,
} from "@/types";

import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/Button";
import { DataTable, type Column } from "@/components/DataTable";
import { DeviceGridCard } from "@/components/DeviceGridCard";
import { DevicesSidebar, type SelectedNode } from "@/components/DevicesSidebar";
import { DeviceReplaceModal } from "@/components/DeviceReplaceModal";
import { Modal } from "@/components/Modal";
import { Field, Input, Select } from "@/components/Field";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Badge } from "@/components/Badge";
import { Card, CardBody, CardHeader } from "@/components/Card";
import { Tip } from "@/components/Tip";
import {
  BatteryIndicator,
  OnlineIndicator,
  SignalIndicator,
} from "@/components/HealthIndicators";
import { roleLabel, shortId, timeAgo } from "@/lib/format";

type ViewMode = "tree" | "table";
const VIEW_MODE_KEY = "envpulse:devices-view-mode";

function loadViewMode(): ViewMode {
  try {
    const raw = localStorage.getItem(VIEW_MODE_KEY);
    return raw === "table" ? "table" : "tree";
  } catch {
    return "tree";
  }
}

/**
 * Papéis de device, com os rótulos genéricos.
 *
 * O VALOR é sempre este — `temp_internal` é `temp_internal` numa geladeira,
 * num balcão térmico e numa sala. O RÓTULO é que muda: "Temperatura
 * interna" não descreve a sala de um data center nem a cuba de um balcão.
 * Quando o ativo escolhido tem tipo, os rótulos vêm do catálogo
 * (GET /equipment-kinds → `rotulos`), e esta lista é o fallback para device
 * órfão ou ativo sem tipo.
 *
 * Papel = o que o sensor mede. Nome do device = onde ele está ("Corredor
 * frio", "Rack 12"). É assim que N sensores no mesmo papel se distinguem,
 * sem precisar inventar papel novo para cada ponto.
 */
const ROLE_OPTIONS = [
  { value: "",                  label: "— não definido —" },
  { value: "temp_internal",     label: "Temperatura interna" },
  { value: "temp_product",      label: "Temperatura do produto" },
  { value: "temp_external",     label: "Temperatura externa" },
  { value: "humidity_internal", label: "Umidade interna" },
  { value: "humidity_external", label: "Umidade externa" },
  { value: "door",              label: "Sensor de porta" },
  { value: "power",             label: "Tomada / energia" },
  { value: "composite",         label: "Multi-sensor (tudo em um)" },
];

const KIND_OPTIONS = [
  { value: "",       label: "(automático)" },
  { value: "sensor", label: "Sensor" },
  { value: "door",   label: "Porta" },
  { value: "meter",  label: "Tomada" },
];

const POWER_SOURCE_OPTIONS: { value: PowerSource; label: string; hint: string }[] = [
  { value: "unknown", label: "— não sei",          hint: "Mensagem de alerta genérica" },
  { value: "ac",      label: "⚡ Rede elétrica (AC)", hint: "Alerta destaca 'falta de energia' quando offline" },
  { value: "battery", label: "🔋 Bateria",          hint: "Alerta destaca 'sensor sem sinal' quando offline" },
];

/**
 * Heurística: dado um role, sugere power_source. Cliente pode override
 * manualmente no dropdown. Alinhada com o backfill da migration v14.
 */
function suggestPowerSourceFromRole(role: string): PowerSource {
  if (role === "power" || role === "composite") return "ac";
  if (
    role === "temp_internal" ||
    role === "temp_product" ||
    role === "temp_external" ||
    role === "humidity_internal" ||
    role === "humidity_external" ||
    role === "door"
  ) {
    return "battery";
  }
  return "unknown";
}

export function AdminDevicesPage() {
  const auth = useAuth();
  const tenants = useTenants(auth.isSuperAdmin);

  // super_admin escolhe um tenant pra filtrar / contextualizar criação
  const [pickedTenantId, setPickedTenantId] = useState<string>("");
  const [showInactive, setShowInactive] = useState(false);
  const tenantFilter = auth.isSuperAdmin ? pickedTenantId || undefined : undefined;

  const devices = useDevices({
    ...(tenantFilter ? { tenant_id: tenantFilter } : {}),
    include_inactive: showInactive,
  });
  const sites = useSites(tenantFilter ? { tenant_id: tenantFilter } : undefined);
  const { rotulo: rotuloDaUnidade, opcoes: opcoesDeUnidade } = useRotuloDeUnidade();
  const equipments = useEquipments({
    ...(tenantFilter ? { tenant_id: tenantFilter } : {}),
    include_archived: true,  // sidebar mostra arquivadas dimmed pra dar contexto
  });
  const orphans = useOrphanDevices();
  const statuses = useDeviceStatuses(tenantFilter ? { tenant_id: tenantFilter } : undefined);
  // Substituição de sensor. `replacePrefill` vem da lista de não vinculados:
  // o gesto nasce ali, quando o sensor novo aparece publicando.
  const [replaceTarget, setReplaceTarget] = useState<Device | null>(null);
  const [replacePrefill, setReplacePrefill] = useState<string | null>(null);

  const qc = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();
  const focusedId = searchParams.get("focus") ?? undefined;

  // Modo de visualização (árvore com split panel vs. tabela plana clássica).
  // Persiste em localStorage — cada operador escolhe seu default.
  const [viewMode, setViewMode] = useState<ViewMode>(() => loadViewMode());
  useEffect(() => {
    try { localStorage.setItem(VIEW_MODE_KEY, viewMode); } catch {}
  }, [viewMode]);

  // Nó selecionado na sidebar (só relevante em viewMode='tree').
  // 'all' = mostra todos os devices; 'site'/'equipment' = filtra;
  // 'orphans' = mostra seção de órfãos.
  const [selectedNode, setSelectedNode] = useState<SelectedNode>({ kind: "all" });

  const [editTarget, setEditTarget] = useState<Device | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Device | null>(null);
  const [detailTarget, setDetailTarget] = useState<Device | null>(null);
  const [siteFilter, setSiteFilter] = useState("");
  const [equipmentFilter, setEquipmentFilter] = useState("");
  const [orphanPrefill, setOrphanPrefill] = useState<{
    site_id?: string;
    equipment_id?: string;
    local_id?: string;
  } | null>(null);

  const tableContainerRef = useRef<HTMLDivElement | null>(null);

  // Map device_id → status pra lookup O(1) na renderização das rows
  const statusByDevice = useMemo(() => {
    const m = new Map<string, DeviceStatus>();
    for (const s of statuses.data ?? []) {
      m.set(s.device_id, s);
    }
    return m;
  }, [statuses.data]);

  // Quando vem ?focus=X na URL: scroll na row + abre o drawer de detalhes
  useEffect(() => {
    if (!focusedId || !devices.data) return;
    const target = devices.data.find((d) => d.id === focusedId);
    if (target) {
      setDetailTarget(target);
      // Aguarda 1 tick pra DOM renderizar antes de scrollar
      const t = setTimeout(() => {
        const el = document.querySelector(
          `[data-row-key="${focusedId}"]`,
        ) as HTMLElement | null;
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 50);
      return () => clearTimeout(t);
    }
  }, [focusedId, devices.data]);

  // No modo tabela usa os filtros clássicos (site/equipment dropdowns).
  // No modo árvore ignora esses dropdowns — quem manda é o nó selecionado
  // na sidebar (deixa o painel direito coerente com o clique da árvore).
  const filtered = useMemo(() => {
    let list = devices.data ?? [];
    if (viewMode === "tree") {
      if (selectedNode.kind === "site") {
        list = list.filter((d) => d.site_id === selectedNode.id);
      } else if (selectedNode.kind === "equipment") {
        list = list.filter((d) => d.equipment_id === selectedNode.id);
      } else if (selectedNode.kind === "orphans") {
        list = [];  // órfãos vêm da API separada (orphans.data)
      }
      // 'all' → sem filtro
    } else {
      if (siteFilter) list = list.filter((d) => d.site_id === siteFilter);
      if (equipmentFilter) list = list.filter((d) => d.equipment_id === equipmentFilter);
    }
    return list;
  }, [devices.data, siteFilter, equipmentFilter, viewMode, selectedNode]);

  const siteNameById = new Map((sites.data ?? []).map((s) => [s.id, rotuloDaUnidade(s)]));
  const equipmentNameById = new Map(
    (equipments.data ?? []).map((e) => [e.id, e.name]),
  );
  // #174: equipment arquivado não é avaliado pelo engine, mas arquivar a
  // ativo NÃO desativa os sensores dela. Sem sinalizar isso, a tela mostra
  // o device como ativo e saudável enquanto ele está, na prática, dormente.
  const equipmentArchivedById = new Map(
    (equipments.data ?? []).map((e) => [e.id, !e.active]),
  );
  const isDormant = (d: { equipment_id?: string | null; active?: boolean }) =>
    !!d.active && !!d.equipment_id && equipmentArchivedById.get(d.equipment_id) === true;
  // Pra super_admin sem filtro: site → tenant → slug pra exibir chip
  const siteToTenantSlug = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of sites.data ?? []) {
      const tenant = (tenants.data ?? []).find((t) => t.id === s.tenant_id);
      if (tenant) m.set(s.id, tenant.slug);
    }
    return m;
  }, [sites.data, tenants.data]);
  const showTenantColumn = auth.isSuperAdmin && !tenantFilter;

  const createMut = useMutation({
    mutationFn: (p: DeviceCreate) => devicesApi.create(p),
    onSuccess: () => {
      toast.success("Device cadastrado");
      void qc.invalidateQueries({ queryKey: ["devices"] });
      void qc.invalidateQueries({ queryKey: qk.orphanDevices() });
      setIsCreating(false);
      setOrphanPrefill(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, p }: { id: string; p: DeviceCreate }) =>
      devicesApi.update(id, {
        equipment_id: p.equipment_id,
        local_id: p.local_id,
        name: p.name,
        kind: p.kind,
        role: p.role,
        metadata: p.metadata,
        active: p.active,
        power_source: p.power_source,
      }),
    onSuccess: () => {
      toast.success("Device atualizado");
      void qc.invalidateQueries({ queryKey: ["devices"] });
      setEditTarget(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => devicesApi.remove(id),
    onSuccess: (r) => {
      toast.success(
        r.acao === "desativado"
          ? "Sensor desativado — o histórico de leituras foi preservado"
          : "Device deletado",
      );
      void qc.invalidateQueries({ queryKey: ["devices"] });
      void qc.invalidateQueries({ queryKey: qk.orphanDevices() });
      setDeleteTarget(null);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const columns: Column<Device>[] = [
    {
      key: "name",
      header: "Nome",
      cell: (r) => (
        <span className="flex items-center gap-2">
          <span className={clsx(
            "font-medium",
            r.active
              ? "text-slate-900 dark:text-slate-100"
              : "text-slate-500 dark:text-slate-500 line-through",
          )}>
            {r.name}
          </span>
          {!r.active && (
            <Badge tone="neutral">Inativo</Badge>
          )}
        </span>
      ),
    },
    {
      key: "role",
      header: "Role",
      cell: (r) => (
        <span className="flex items-center gap-1.5">
          <Badge tone="brand">{roleLabel(r.role)}</Badge>
          {r.power_source === "ac" && (
            <span title="Rede elétrica (AC)" className="text-amber-600 dark:text-amber-400" aria-label="AC">⚡</span>
          )}
          {r.power_source === "battery" && (
            <span title="Bateria" className="text-emerald-600 dark:text-emerald-400" aria-label="Bateria">🔋</span>
          )}
        </span>
      ),
    },
    { key: "local_id", header: "Identificador", cell: (r) => <code className="text-xs text-slate-600 dark:text-slate-400">{r.local_id}</code> },
    {
      key: "status",
      header: "Status",
      cell: (r) => <OnlineIndicator online={statusByDevice.get(r.id)?.online} />,
    },
    {
      key: "battery",
      header: "Bateria",
      cell: (r) => <BatteryIndicator pct={statusByDevice.get(r.id)?.battery_pct ?? null} size="sm" />,
    },
    {
      key: "signal",
      header: "Sinal",
      cell: (r) => {
        const s = statusByDevice.get(r.id);
        return (
          <SignalIndicator
            dbm={s?.signal_dbm ?? null}
            lqi={s?.signal_lqi ?? null}
            size="sm"
          />
        );
      },
    },
    {
      key: "site",
      header: "Site",
      hideOnMobile: true,
      cell: (r) => (
        <div className="flex items-center gap-2">
          <span className="text-slate-600 dark:text-slate-400">{siteNameById.get(r.site_id) ?? shortId(r.site_id)}</span>
          {showTenantColumn && siteToTenantSlug.get(r.site_id) && (
            <span className="rounded bg-amber-100 dark:bg-amber-900/40 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800 dark:text-amber-300">
              {siteToTenantSlug.get(r.site_id)}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "equipment",
      header: "Ativo",
      cell: (r) =>
        r.equipment_id ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="text-slate-600 dark:text-slate-400">
              {equipmentNameById.get(r.equipment_id) ?? shortId(r.equipment_id)}
            </span>
            {equipmentArchivedById.get(r.equipment_id) === true && (
              <span title="Ativo arquivado — este sensor não gera alertas">
                <Badge tone="warning">arquivada</Badge>
              </span>
            )}
          </span>
        ) : (
          <Badge tone="warning">sem ativo</Badge>
        ),
      hideOnMobile: true,
    },
    {
      key: "actions",
      header: "",
      align: "right",
      cell: (r) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => setDetailTarget(r)}>
            Detalhes
          </Button>
          <Button size="sm" variant="ghost" icon={<Pencil className="h-4 w-4" />} onClick={() => setEditTarget(r)}>
            Editar
          </Button>
          {r.active && r.equipment_id && (
            <Button
              size="sm"
              variant="ghost"
              icon={<Replace className="h-4 w-4" />}
              onClick={() => { setReplacePrefill(null); setReplaceTarget(r); }}
            >
              Substituir
            </Button>
          )}
          <Button size="sm" variant="ghost" icon={<Trash2 className="h-4 w-4 text-red-500" />} onClick={() => setDeleteTarget(r)} />
        </div>
      ),
    },
  ];

  const equipmentOptionsForFilter = useMemo(() => {
    let list = equipments.data ?? [];
    if (siteFilter) list = list.filter((e) => e.site_id === siteFilter);
    return list.map((e) => ({ value: e.id, label: e.name }));
  }, [equipments.data, siteFilter]);

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title="Devices"
        description="Hardware físico (probes, tomadas, sensores de porta)."
        actions={
          <div className="flex items-center gap-2">
            {/* Toggle Árvore | Tabela — persiste em localStorage */}
            <div className="inline-flex rounded-md border border-surface-200 dark:border-slate-700 bg-white dark:bg-slate-900">
              <button
                type="button"
                onClick={() => setViewMode("tree")}
                className={clsx(
                  "flex items-center gap-1.5 rounded-l-md px-3 py-1.5 text-xs font-medium",
                  viewMode === "tree"
                    ? "bg-brand-600 text-white"
                    : "text-slate-600 dark:text-slate-300 hover:bg-surface-100 dark:hover:bg-slate-800",
                )}
                title="Visão hierárquica com sidebar"
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                Árvore
              </button>
              <button
                type="button"
                onClick={() => setViewMode("table")}
                className={clsx(
                  "flex items-center gap-1.5 rounded-r-md px-3 py-1.5 text-xs font-medium",
                  viewMode === "table"
                    ? "bg-brand-600 text-white"
                    : "text-slate-600 dark:text-slate-300 hover:bg-surface-100 dark:hover:bg-slate-800",
                )}
                title="Tabela plana com filtros"
              >
                <Rows3 className="h-3.5 w-3.5" />
                Tabela
              </button>
            </div>
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setIsCreating(true)}>
              Novo device
            </Button>
          </div>
        }
      />

      {/* Seletor de tenant (super_admin) */}
      {auth.isSuperAdmin && (
        <Card className="mb-4">
          <CardBody>
            <Field
              label={
                <span className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-brand-600" />
                  Tenant
                </span>
              }
            >
              <Select
                value={pickedTenantId}
                onChange={(e) => {
                  setPickedTenantId(e.target.value);
                  setSiteFilter("");
                  setEquipmentFilter("");
                }}
                options={[
                  { value: "", label: "Todos os tenants" },
                  ...(tenants.data ?? []).map((t) => ({
                    value: t.id,
                    label: `${t.name} (${t.slug})`,
                  })),
                ]}
              />
            </Field>
          </CardBody>
        </Card>
      )}

      {/* Toggle "mostrar inativos" visível nos dois modos — flag global. */}
      <div className="mb-3 flex justify-end">
        <label className="inline-flex items-center gap-2 rounded-md border border-surface-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-xs">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-surface-300 dark:border-slate-700 text-brand-600"
          />
          <span className="text-slate-700 dark:text-slate-300 whitespace-nowrap">
            Mostrar inativos
          </span>
        </label>
      </div>

      {viewMode === "table" ? (
        <>
          {/* Filtros do modo tabela (dropdowns clássicos) */}
          <div className="mb-4 grid gap-3 sm:grid-cols-2">
            <Field label="Site">
              <Select
                value={siteFilter}
                onChange={(e) => {
                  setSiteFilter(e.target.value);
                  setEquipmentFilter("");
                }}
                options={[
                  { value: "", label: "Todos" },
                  ...opcoesDeUnidade(sites.data),
                ]}
              />
            </Field>
            <Field label="Ativo">
              <Select
                value={equipmentFilter}
                onChange={(e) => setEquipmentFilter(e.target.value)}
                options={[{ value: "", label: "Todas" }, ...equipmentOptionsForFilter]}
              />
            </Field>
          </div>

          <div ref={tableContainerRef}>
            <DataTable
              data={filtered}
              columns={columns}
              loading={devices.isLoading}
              rowKey={(r) => r.id}
              emptyTitle="Nenhum device cadastrado"
              highlightedRowKey={focusedId}
            />
          </div>
        </>
      ) : (
        <TreeSplitPanel
          sites={sites.data ?? []}
          equipments={equipments.data ?? []}
          allDevices={devices.data ?? []}
          filteredDevices={filtered}
          statusByDevice={statusByDevice}
          orphans={orphans.data ?? []}
          selected={selectedNode}
          onSelect={setSelectedNode}
          onDetails={(d) => setDetailTarget(d)}
          onEdit={(d) => setEditTarget(d)}
          onDelete={(d) => setDeleteTarget(d)}
          onCreate={() => {
            // Prefill contextual: se estiver dentro de um site/equipment,
            // pré-preenche esses campos no form (Fase B do split panel #151).
            if (selectedNode.kind === "equipment") {
              const eq = (equipments.data ?? []).find((e) => e.id === selectedNode.id);
              if (eq) {
                setOrphanPrefill({
                  site_id: eq.site_id,
                  equipment_id: eq.id,
                });
              }
            } else if (selectedNode.kind === "site") {
              setOrphanPrefill({ site_id: selectedNode.id });
            } else {
              setOrphanPrefill(null);  // "Todos" — sem prefill
            }
            setIsCreating(true);
          }}
          onCreateOrphan={(o) => {
            const site = (sites.data ?? []).find((s) => s.slug === o.site_slug);
            setOrphanPrefill({ site_id: site?.id, local_id: o.local_id });
            setIsCreating(true);
          }}
        />
      )}

      {/* Orphans — só no modo tabela (no tree eles moram na sidebar/painel) */}
      {viewMode === "table" && orphans.data && orphans.data.length > 0 && (
        <Card className="mt-8 border-amber-200 dark:border-amber-900/60 bg-amber-50/30 dark:!bg-amber-950/30">
          <CardHeader
            title={
              <span className="flex items-center gap-2 text-amber-800 dark:text-amber-300">
                <AlertTriangle className="h-4 w-4" />
                Devices órfãos ({orphans.data.length})
              </span>
            }
            subtitle="Sensores detectados mas ainda não cadastrados. Vincule pra começar a receber leituras."
          />
          <CardBody className="p-0">
            <ul className="divide-y divide-amber-200">
              {orphans.data.map((o) => {
                const site = (sites.data ?? []).find((s) => s.slug === o.site_slug);
                return (
                  <li
                    key={`${o.tenant_slug}/${o.site_slug}/${o.local_id}`}
                    className="flex items-center justify-between gap-3 px-5 py-3 text-sm"
                  >
                    <div>
                      <code className="font-mono text-xs text-slate-700 dark:text-slate-300">
                        {o.tenant_slug}/{o.site_slug}/{o.local_id}
                      </code>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {o.sample_count} amostras · última {timeAgo(o.last_seen)}
                      </div>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          // Quem SAI ainda não foi escolhido: abre com o
                          // primeiro sensor ativo e vinculado do mesmo site, e
                          // a pessoa troca no modal se não for esse.
                          const candidato = (devices.data ?? []).find(
                            (d) => d.site_id === site?.id && d.active && d.equipment_id,
                          );
                          if (!candidato) {
                            toast.error("Nenhum sensor vinculado neste ativo para substituir");
                            return;
                          }
                          setReplacePrefill(o.local_id);
                          setReplaceTarget(candidato);
                        }}
                      >
                        Substituir
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setOrphanPrefill({
                            site_id: site?.id,
                            local_id: o.local_id,
                          });
                          setIsCreating(true);
                        }}
                      >
                        Cadastrar
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </CardBody>
        </Card>
      )}

      <DeviceDetailModal
        device={detailTarget}
        status={detailTarget ? statusByDevice.get(detailTarget.id) ?? null : null}
        siteName={detailTarget ? siteNameById.get(detailTarget.site_id) ?? null : null}
        equipmentName={
          detailTarget?.equipment_id
            ? equipmentNameById.get(detailTarget.equipment_id) ?? null
            : null
        }
        equipmentArchived={detailTarget ? isDormant(detailTarget) : false}
        onClose={() => {
          setDetailTarget(null);
          // limpa o ?focus= da URL pra não re-abrir em refresh
          if (focusedId) {
            const next = new URLSearchParams(searchParams);
            next.delete("focus");
            setSearchParams(next, { replace: true });
          }
        }}
      />

      <DeviceFormModal
        key={
          isCreating
            ? `create-${orphanPrefill?.site_id ?? ""}-${orphanPrefill?.equipment_id ?? ""}-${orphanPrefill?.local_id ?? ""}`
            : `edit-${editTarget?.id ?? "none"}`
        }
        open={isCreating || !!editTarget}
        onClose={() => {
          setIsCreating(false);
          setEditTarget(null);
          setOrphanPrefill(null);
        }}
        title={isCreating ? "Novo device" : "Editar device"}
        initial={editTarget}
        prefill={orphanPrefill ?? undefined}
        siteOptions={opcoesDeUnidade(sites.data)}
        allEquipments={equipments.data ?? []}
        submitting={createMut.isPending || updateMut.isPending}
        onSubmit={(payload) => {
          if (editTarget) {
            updateMut.mutate({ id: editTarget.id, p: payload });
          } else {
            createMut.mutate(payload);
          }
        }}
      />

      <DeviceReplaceModal
        sai={replaceTarget}
        entraLocalId={replacePrefill}
        candidatos={(devices.data ?? []).filter(
          (d) => d.site_id === replaceTarget?.site_id && !d.equipment_id,
        )}
        orfaos={orphans.data ?? []}
        onClose={() => { setReplaceTarget(null); setReplacePrefill(null); }}
        onDone={() => {
          toast.success("Sensor substituído — o histórico do ativo foi preservado");
          void qc.invalidateQueries({ queryKey: ["devices"] });
          void qc.invalidateQueries({ queryKey: qk.orphanDevices() });
          setReplaceTarget(null);
          setReplacePrefill(null);
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Remover device?"
        message={
          deleteTarget
            ? `Se '${deleteTarget.name}' já registrou leituras, ele será DESATIVADO em vez de apagado — continua na lista, riscado, e o histórico segue ligado a ele. Só um cadastro que nunca mediu nada é apagado de verdade. Se o sensor continuar ativo no local, ele reaparece na seção "Não vinculados".`
            : ""
        }
        confirmLabel="Remover"
        loading={deleteMut.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
      />
    </div>
  );
}

// ---------- detail modal ----------------------------------------------------

function DeviceDetailModal({
  device,
  status,
  siteName,
  equipmentName,
  equipmentArchived,
  onClose,
}: {
  device: Device | null;
  status: DeviceStatus | null;
  siteName: string | null;
  equipmentName: string | null;
  equipmentArchived?: boolean;
  onClose: () => void;
}) {
  return (
    <Modal
      open={!!device}
      onClose={onClose}
      title={device?.name ?? ""}
      description={device ? `${roleLabel(device.role)} · ${device.local_id}` : undefined}
      size="lg"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      {device && (
        <div className="space-y-4">
          {/* Aviso device inativo (destaque acima de tudo) */}
          {!device.active && (
            <div className="rounded-lg border border-slate-300 dark:border-slate-700 bg-slate-100/60 dark:bg-slate-800/60 p-3 text-sm">
              <div className="font-semibold text-slate-700 dark:text-slate-300">
                Device inativo
              </div>
              <div className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">
                {device.deactivated_at
                  ? `Desativado há ${timeAgo(device.deactivated_at)}. `
                  : "Marcado como inativo. "}
                Métricas abaixo são as últimas registradas antes da desativação —
                não refletem estado atual.
              </div>
            </div>
          )}

          {/* Aviso stale (leituras muito antigas mesmo com active=true) */}
          {device.active && status?.is_stale && (
            <div className="rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-950/40 p-3 text-sm">
              <div className="font-semibold text-amber-800 dark:text-amber-300">
                Sem leituras recentes
              </div>
              <div className="mt-0.5 text-xs text-amber-700 dark:text-amber-400">
                Última leitura {status.last_seen ? `há ${timeAgo(status.last_seen)}` : "desconhecida"}.
                Bateria/sinal exibidos abaixo são valores antigos —
                verifique se o device ainda está no local.
              </div>
            </div>
          )}

          {/* Saúde */}
          <div className="rounded-lg border border-surface-200 dark:border-slate-800 bg-surface-50/50 dark:bg-slate-800/40 p-4">
            <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Saúde
              {(!device.active || status?.is_stale) && (
                <span className="ml-2 text-slate-400 dark:text-slate-500 normal-case font-normal">
                  (histórica)
                </span>
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              <DetailMetric label="Status">
                <OnlineIndicator online={status?.online} />
              </DetailMetric>
              <DetailMetric label="Bateria">
                <BatteryIndicator pct={status?.battery_pct ?? null} />
              </DetailMetric>
              <DetailMetric label="Sinal">
                <SignalIndicator
                  dbm={status?.signal_dbm ?? null}
                  lqi={status?.signal_lqi ?? null}
                />
              </DetailMetric>
            </div>
            {(status?.last_seen || status?.last_lwt) && (
              <div className="mt-3 grid gap-2 text-xs text-slate-500 dark:text-slate-400 sm:grid-cols-3">
                {status.last_seen && (
                  <div>
                    Última leitura: <span className="text-slate-700 dark:text-slate-300">{timeAgo(status.last_seen)}</span>
                  </div>
                )}
                {status.last_lwt && (
                  <div>
                    Último LWT: <span className="text-slate-700 dark:text-slate-300">{timeAgo(status.last_lwt)} ({status.last_lwt_msg})</span>
                  </div>
                )}
                {status.last_state && (
                  <div>
                    Último STATE: <span className="text-slate-700 dark:text-slate-300">{timeAgo(status.last_state)}</span>
                  </div>
                )}
                {status.uptime_sec !== null && status.uptime_sec !== undefined && (
                  <div>
                    Uptime: <span className="text-slate-700 dark:text-slate-300">{formatUptime(status.uptime_sec)}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Cadastro */}
          <div className="grid gap-3 text-sm sm:grid-cols-2">
            <KV label="ID" value={<code className="text-xs">{shortId(device.id)}</code>} />
            <KV label="Kind" value={device.kind ?? "—"} />
            <KV label="Site" value={siteName ?? shortId(device.site_id)} />
            <KV
              label="Ativo"
              value={
                device.equipment_id
                  ? (
                    <span className="inline-flex items-center gap-1.5">
                      {equipmentName ?? shortId(device.equipment_id)}
                      {equipmentArchived && <Badge tone="warning">arquivada</Badge>}
                    </span>
                  )
                  : <span className="text-amber-700">sem ativo</span>
              }
            />
          </div>

          {/* #174: o sensor está ativo, mas o ativo arquivado tira ele de
              todas as avaliações do engine. Dizer isso em texto evita que
              alguém confie num sensor que não vai alertar. */}
          {equipmentArchived && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900
                            dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-200">
              Este sensor está ativo, mas o ativo a que ele pertence está
              arquivada — nenhum alerta será disparado para ele. Reative a
              ativo para voltar a monitorar, ou desative o sensor se ele foi
              removido de campo.
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function DetailMetric({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-surface-100 dark:border-slate-800 pb-2 last:border-b-0">
      <span className="text-xs text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-sm text-slate-800 dark:text-slate-200">{value}</span>
    </div>
  );
}

function formatUptime(seconds: number): string {
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

// ---------- form modal -------------------------------------------------------

function DeviceFormModal({
  open,
  onClose,
  title,
  initial,
  prefill,
  siteOptions,
  allEquipments,
  submitting,
  onSubmit,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  initial?: Device | null;
  prefill?: { site_id?: string; equipment_id?: string; local_id?: string };
  siteOptions: { value: string; label: string }[];
  allEquipments: { id: string; site_id: string; name: string }[];
  submitting?: boolean;
  onSubmit: (p: DeviceCreate) => void;
}) {
  const [siteId, setSiteId] = useState(
    initial?.site_id ?? prefill?.site_id ?? siteOptions[0]?.value ?? "",
  );
  const [equipmentId, setEquipmentId] = useState<string>(
    initial?.equipment_id ?? prefill?.equipment_id ?? "",
  );
  const [localId, setLocalId] = useState(initial?.local_id ?? prefill?.local_id ?? "");
  const [name, setName] = useState(initial?.name ?? "");
  const [role, setRole] = useState(initial?.role ?? "");

  // Rótulos do papel seguem o tipo do ativo escolhido no próprio
  // formulário — trocar o ativo reetiqueta as opções na hora.
  const kindsCat = useEquipmentKinds();
  const equipmentsCat = useEquipments();
  const roleOptions = useMemo(() => {
    const eq = (equipmentsCat.data ?? []).find((e) => e.id === equipmentId);
    const tipo = (kindsCat.data ?? []).find((k) => k.slug === eq?.kind);
    if (!tipo) return ROLE_OPTIONS;
    return ROLE_OPTIONS.map((o) =>
      o.value ? { ...o, label: tipo.rotulos[o.value] ?? o.label } : o);
  }, [equipmentId, equipmentsCat.data, kindsCat.data]);
  const [kind, setKind] = useState(initial?.kind ?? "");
  const [active, setActive] = useState<boolean>(initial?.active ?? true);
  const [powerSource, setPowerSource] = useState<PowerSource>(
    initial?.power_source ?? "unknown",
  );
  // Rastreia se user MEXEU no dropdown. Se sim, para de auto-sugerir a partir
  // do role (respeita override). Se não, autofill segue o role ativo.
  const [powerSourceTouched, setPowerSourceTouched] = useState(false);
  // #186 — manter acordado. Guardado em metadata.keepalive; a receita (o que
  // escrever, de quanto em quanto) mora no consumer, não aqui: é
  // conhecimento de hardware, e a tela só decide SE aplica.
  const [keepalive, setKeepalive] = useState<boolean>(
    keepaliveLigado(initial?.metadata),
  );

  useEffect(() => {
    setSiteId(initial?.site_id ?? prefill?.site_id ?? siteOptions[0]?.value ?? "");
    setEquipmentId(initial?.equipment_id ?? prefill?.equipment_id ?? "");
    setLocalId(initial?.local_id ?? prefill?.local_id ?? "");
    setName(initial?.name ?? "");
    setRole(initial?.role ?? "");
    setKind(initial?.kind ?? "");
    setActive(initial?.active ?? true);
    setPowerSource(initial?.power_source ?? "unknown");
    setPowerSourceTouched(false);
    setKeepalive(keepaliveLigado(initial?.metadata));
  }, [initial, prefill, siteOptions]);

  // Auto-sugere power_source ao trocar role — só se user não editou manualmente
  // e ainda está no default 'unknown' (não sobrescreve override existente).
  useEffect(() => {
    if (powerSourceTouched) return;
    if (initial) return; // no modo edição, não muda o que o cliente já configurou
    if (!role) return;
    const suggested = suggestPowerSourceFromRole(role);
    if (suggested !== "unknown") setPowerSource(suggested);
  }, [role, powerSourceTouched, initial]);

  const equipmentOptions = useMemo(() => {
    const list = allEquipments.filter((e) => e.site_id === siteId);
    return [
      { value: "", label: "(sem ativo)" },
      ...list.map((e) => ({ value: e.id, label: e.name })),
    ];
  }, [allEquipments, siteId]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit({
      site_id: siteId,
      equipment_id: equipmentId || null,
      local_id: localId,
      name,
      role: role || null,
      kind: kind || null,
      active,
      power_source: powerSource,
      metadata: mesclarKeepalive(initial?.metadata, keepalive),
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" form="device-form" loading={submitting}>
            Salvar
          </Button>
        </>
      }
    >
      <form id="device-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Site" required>
            <Select
              value={siteId}
              onChange={(e) => {
                setSiteId(e.target.value);
                setEquipmentId("");
              }}
              required
              options={siteOptions}
              placeholder="Selecione o site"
              disabled={!!initial}
            />
          </Field>
          <Field label="Ativo">
            <Select
              value={equipmentId}
              onChange={(e) => setEquipmentId(e.target.value)}
              options={equipmentOptions}
            />
          </Field>
        </div>

        <Field label="Nome" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Sensor Interno" />
        </Field>

        <Field
          label={
            <>
              Identificador
              <Tip label="Identificador">
                Código único do sensor, como ele se apresenta na rede.
              <strong>Não pode ser alterado depois</strong> — as leituras já
              gravadas ficam presas a ele.
              </Tip>
            </>
          }
          required
        >
          <Input
            value={localId}
            onChange={(e) => setLocalId(e.target.value)}
            required
            placeholder="0xA1B2"
            disabled={!!initial}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Função">
            <Select value={role} onChange={(e) => setRole(e.target.value)} options={roleOptions} />
          </Field>
          <Field label="Tipo">
            <Select value={kind} onChange={(e) => setKind(e.target.value)} options={KIND_OPTIONS} />
          </Field>
        </div>

        <Field
          label="Fonte de energia"
          hint={
            POWER_SOURCE_OPTIONS.find((o) => o.value === powerSource)?.hint ??
            "Usado pra contextualizar alertas de offline (AC → 'falta de energia'; battery → 'sensor sem sinal')."
          }
        >
          <Select
            value={powerSource}
            onChange={(e) => {
              setPowerSource(e.target.value as PowerSource);
              setPowerSourceTouched(true);
            }}
            options={POWER_SOURCE_OPTIONS.map((o) => ({
              value: o.value,
              label: o.label,
            }))}
          />
        </Field>

        {/* Toggle Ativo — só aparece no modo edição.
            Cadastro novo sempre entra ativo (default do backend). */}
        {initial && (
          <div className="rounded-md border border-surface-200 dark:border-slate-800 bg-surface-50/50 dark:bg-slate-800/40 p-3">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-surface-300 dark:border-slate-700 text-brand-600"
              />
              <div className="text-sm">
                <div className="font-medium text-slate-700 dark:text-slate-300">
                  Device ativo
                </div>
                <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                  Desmarque se o device foi desinstalado do local. O cadastro
                  fica preservado (histórico intacto), mas ele deixa de aparecer
                  no dashboard, engine de alertas e badges de saúde.
                </div>
              </div>
            </label>
          </div>
        )}

        {initial && (
          <KeepaliveField
            deviceId={initial.id}
            checked={keepalive}
            onChange={setKeepalive}
          />
        )}
      </form>
    </Modal>
  );
}

interface KeepaliveMeta {
  keepalive?: { enabled?: boolean };
}

/**
 * Ligado? A regra tem que ser IDÊNTICA à do consumer (`keepalive.resolver`):
 * a chave presente vale como ligada, e só `enabled: false` desliga.
 *
 * Escrevi isto errado da primeira vez — `Boolean(keepalive?.enabled)` — e o
 * primeiro device configurado à mão, que tem a chave sem `enabled`, apareceu
 * com a caixa desmarcada enquanto o keepalive batia normalmente. Front e
 * backend discordando sobre o significado da mesma configuração é a tela
 * mentindo, que é justamente o que este campo existe pra evitar.
 */
function keepaliveLigado(metadata: Record<string, unknown> | undefined): boolean {
  const ka = (metadata as KeepaliveMeta | undefined)?.keepalive;
  return ka !== undefined && ka.enabled !== false;
}

/**
 * Mescla o toggle no metadata existente.
 *
 * Mesclar e não substituir: metadata carrega outras chaves (o registro de
 * troca de sensor, por exemplo) e mandar só a nossa apagaria o resto.
 *
 * Desligar grava `enabled: false` em vez de remover a chave, pra que quem
 * tiver afinado intervalo ou pausa não perca o ajuste ao religar. Mas se
 * nunca houve keepalive e o toggle está desligado, não escreve nada — não
 * há por que carimbar a chave em todo device novo do parque.
 */
function mesclarKeepalive(
  atual: Record<string, unknown> | undefined,
  ligado: boolean,
): Record<string, unknown> {
  const base = atual ?? {};
  const anterior = (base as KeepaliveMeta).keepalive;
  if (!ligado && !anterior) return { ...base };
  return { ...base, keepalive: { ...(anterior ?? {}), enabled: ligado } };
}

/**
 * Manter acordado (#186).
 *
 * Alguns medidores dormem depois de um tempo sem receber escrita do
 * controlador e passam a repetir a última leitura — continuando a responder,
 * com o painel verde. Medido no Tongou TO-Q-SA1: 30 minutos exatos após cada
 * energização, quatro reproduções.
 *
 * Ligar num sensor que não tem esse comportamento é inofensivo: o gateway
 * descarta o comando que a definição dele não conhece. Por isso o toggle não
 * pergunta o modelo — quem instalou sabe qual sensor é.
 *
 * O indicador de confirmação existe porque o toggle PODE MENTIR, e essa é a
 * parte que importa: se o gateway estiver sem o converter que expõe o campo
 * `online_state`, as escritas saem, são descartadas do outro lado e o
 * medidor dorme igual — sem nenhum erro em lugar nenhum, porque MQTT não
 * devolve recusa. A presença do campo na última leitura é a única evidência
 * local de que o caminho está inteiro.
 */
function KeepaliveField({
  deviceId,
  checked,
  onChange,
}: {
  deviceId: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  const summary = useDeviceSummary(deviceId);
  const payload = summary.data?.payload ?? null;
  // Só afirma "não suportado" com leitura em mãos. Sem payload não se sabe,
  // e chutar aqui seria repetir o erro que este campo existe pra evitar.
  const suportado =
    payload === null ? null : Object.hasOwn(payload, "online_state");

  return (
    <div className="rounded-md border border-surface-200 bg-surface-50/50 p-3 dark:border-slate-800 dark:bg-slate-800/40">
      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-surface-300 text-brand-600 dark:border-slate-700"
        />
        <div className="text-sm">
          <div className="font-medium text-slate-700 dark:text-slate-300">
            Manter acordado
          </div>
          <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Escreve no sensor a cada 10 minutos para impedir que ele entre em
            modo de espera e passe a repetir a última leitura. Em sensores que
            não precisam disso, não faz efeito nenhum.
          </div>
          {checked && suportado === false && (
            <div className="mt-2 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              Este sensor não está reportando o campo usado pelo keepalive. Se
              ele for um modelo que precisa disso, falta o converter no
              gateway — e as escritas não terão efeito.
            </div>
          )}
          {checked && suportado === true && (
            <div className="mt-2 text-xs text-emerald-700 dark:text-emerald-400">
              Confirmado: o sensor reporta o campo usado pelo keepalive.
            </div>
          )}
        </div>
      </label>
    </div>
  );
}

// ---------- Tree split panel ------------------------------------------------

interface TreeSplitPanelProps {
  sites: Site[];
  equipments: Equipment[];
  allDevices: Device[];  // pra calcular contagens agregadas por site/equipment
  filteredDevices: Device[];  // devices que caem no nó selecionado
  statusByDevice: Map<string, DeviceStatus>;
  orphans: OrphanDevice[];
  selected: SelectedNode;
  onSelect: (node: SelectedNode) => void;
  onDetails: (d: Device) => void;
  onEdit: (d: Device) => void;
  onDelete: (d: Device) => void;
  onCreate: () => void;
  onCreateOrphan: (o: OrphanDevice) => void;
}

function TreeSplitPanel({
  sites, equipments, allDevices, filteredDevices, statusByDevice,
  orphans, selected, onSelect, onDetails, onEdit, onDelete, onCreate, onCreateOrphan,
}: TreeSplitPanelProps) {
  // Título do painel direito depende do nó selecionado
  const rightTitle = (() => {
    if (selected.kind === "all") return "Todos os devices";
    if (selected.kind === "site") {
      const s = sites.find((x) => x.id === selected.id);
      return s?.name ?? "Site";
    }
    if (selected.kind === "equipment") {
      const e = equipments.find((x) => x.id === selected.id);
      return e?.name ?? "Ativo";
    }
    if (selected.kind === "orphans") return "Devices órfãos";
    return "";
  })();

  // Texto do botão "novo device" fica contextual e explícito no destino do
  // prefill. Em "Todos" o botão do painel direito não aparece (redundância
  // com o "Novo device" do PageHeader).
  const canCreateContextual =
    selected.kind === "site" || selected.kind === "equipment";
  const createButtonLabel =
    selected.kind === "site"
      ? `Novo device em ${rightTitle}`
      : selected.kind === "equipment"
        ? `Novo device em ${rightTitle}`
        : "Novo device";

  return (
    <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
      {/* Sidebar esquerda */}
      <Card>
        <CardBody className="p-3">
          <DevicesSidebar
            sites={sites}
            equipments={equipments}
            devices={allDevices}
            statusByDevice={statusByDevice}
            orphansCount={orphans.length}
            selected={selected}
            onSelect={onSelect}
          />
        </CardBody>
      </Card>

      {/* Painel direito — cards de devices ou lista de órfãos */}
      <div className="min-w-0">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              {rightTitle}
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {selected.kind === "orphans"
                ? `${orphans.length} sensor(es) detectados aguardando cadastro`
                : `${filteredDevices.length} device(s)`}
            </p>
          </div>
          {canCreateContextual && (
            <Button size="sm" icon={<Plus className="h-3.5 w-3.5" />} onClick={onCreate}>
              {createButtonLabel}
            </Button>
          )}
        </div>

        {selected.kind === "orphans" ? (
          <Card className="border-amber-200 dark:border-amber-900/60 bg-amber-50/30 dark:!bg-amber-950/30">
            <CardBody className="p-0">
              <ul className="divide-y divide-amber-200 dark:divide-amber-900/40">
                {orphans.map((o) => (
                  <li
                    key={`${o.tenant_slug}/${o.site_slug}/${o.local_id}`}
                    className="flex items-center justify-between gap-3 px-5 py-3 text-sm"
                  >
                    <div>
                      <code className="font-mono text-xs text-slate-700 dark:text-slate-300">
                        {o.tenant_slug}/{o.site_slug}/{o.local_id}
                      </code>
                      <div className="text-xs text-slate-500 dark:text-slate-400">
                        {o.sample_count} amostras · última {timeAgo(o.last_seen)}
                      </div>
                    </div>
                    <Button size="sm" variant="secondary" onClick={() => onCreateOrphan(o)}>
                      Cadastrar
                    </Button>
                  </li>
                ))}
              </ul>
            </CardBody>
          </Card>
        ) : filteredDevices.length === 0 ? (
          <Card>
            <CardBody className="py-10 text-center text-sm text-slate-500 dark:text-slate-400">
              Nenhum device{" "}
              {selected.kind === "site" || selected.kind === "equipment"
                ? "nesta seleção"
                : "cadastrado"}
              .
            </CardBody>
          </Card>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {filteredDevices.map((d) => (
              <DeviceGridCard
                key={d.id}
                device={d}
                status={statusByDevice.get(d.id) ?? null}
                equipmentArchived={
                  !!d.active && !!d.equipment_id
                  && equipments.find((e) => e.id === d.equipment_id)?.active === false
                }
                onDetails={() => onDetails(d)}
                onEdit={() => onEdit(d)}
                onDelete={() => onDelete(d)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
